export type ReservationRequestStatus =
  | "pending"
  | "ignored"
  | "rejected"
  | "confirmed"
  | "cancelled_by_host"
  | "cancelled_by_user";
export type ReservationRequestKind = "new" | "edit";

export const RESERVATION_DECLINE_REASONS = [
  "fully_booked",
  "party_too_large",
  "outside_hours",
  "table_unavailable",
  "area_fully_booked",
] as const;
export type ReservationDeclineReason =
  (typeof RESERVATION_DECLINE_REASONS)[number];
export const RESERVATION_ACCEPT_REASONS = ["kitchen_closing"] as const;

export type DepositStatus =
  | "none"
  | "pending"
  | "authorized"
  | "captured"
  | "released"
  | "failed";

export type ReservationRequest = {
  id: string;
  restaurant_id: number;
  customer_id: string;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  requested_time: string; // ISO timestamp string
  duration_mins: number;
  request_kind?: ReservationRequestKind;
  target_reservation_id?: string | null;
  target_reservation_hide_duration?: boolean | null;
  reservation_id?: string | null;
  status: ReservationRequestStatus;
  party_size: number;
  notes?: string | null;
  party_name: string;
  party_email: string;
  party_phone?: string | null;
  deposit_amount?: number | null;
  deposit_gateway_id?: string | null;
  deposit_status?: DepositStatus | null;
  requested_tags?: ReservationTagSummary[];
  response_reason: string | null;
  response_note: string | null;
  created_at: string;
  updated_at: string;
};

export type ReservationTableAssignment = {
  id: string;
  table_id: number;
  table_name: string;
  table_capacity: number;
  user_id?: number | null;
  user_name?: string | null;
  status: string;
};

export type ReservationStatus =
  | "confirmed"
  | "no-show"
  | "seated"
  | "arrived"
  | "partially-seated"
  | "cancelled_by_host"
  | "cancelled_by_user"
  | "completed";

// ── Status groups — the single source of truth for "which statuses count as
// what". Previously these arrays were hand-rolled inline ~20 times across the
// web app and had drifted (e.g. "changed" dropped from some, "arrived" from
// others). Import these instead of re-typing the literals.
//
// CONFIRMED_STATUSES   — booked but nobody has shown up yet.
//                        has_changed=true means the booking was edited in-place.
// SEATED_STATUSES      — the party is physically at the table.
// ACTIVE_TABLE_STATUSES— anything that occupies/holds a table (confirmed+seated);
//                        used for floor occupancy, counts and conflict detection.
// CANCELLED_STATUSES   — the booking no longer holds a table.
export const CONFIRMED_STATUSES = ["confirmed"] as const;
export const SEATED_STATUSES = [
  "seated",
  "arrived",
  "partially-seated",
] as const;
export const ACTIVE_TABLE_STATUSES = [
  ...CONFIRMED_STATUSES,
  ...SEATED_STATUSES,
] as const;
export const CANCELLED_STATUSES = [
  "no-show",
  "cancelled_by_host",
  "cancelled_by_user",
] as const;
// Every reservation status — the single list to build validation enums / filters
// from, so adding a status is a one-line change (kept in sync with the
// ReservationStatus union above).
export const ALL_RESERVATION_STATUSES = [
  ...CONFIRMED_STATUSES,
  ...SEATED_STATUSES,
  "completed",
  ...CANCELLED_STATUSES,
] as const;
export type ReservationTagSummary = {
  id: string;
  name: string;
  color?: string | null;
  icon?: string | null;
};

export type Reservation = {
  id: string;
  restaurant_id: number;
  request_id?: string | null;
  customer_id?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  time: string;
  duration_mins: number;
  status: ReservationStatus;
  party_size: number;
  notes?: string | null;
  party_name: string;
  party_email: string;
  party_phone?: string | null;
  created_at: string;
  updated_at: string;
  deposit_amount?: number | null;
  deposit_gateway_id?: string | null;
  deposit_status?: DepositStatus | null;
  has_changed?: boolean | null;
  hide_duration_to_guests?: boolean | null;
  table_assignments: ReservationTableAssignment[];
  comments: ReservationComment[];
  tags: ReservationTagSummary[];
  requested_tags?: ReservationTagSummary[];
  source?: "phone" | "online";
};

export type ReservationTable = {
  id: string;
  reservation_id: string;
  table_id: number;
  user_id?: number;
};

export type TimeSlotAvailability = {
  time: string; // HH:MM format (e.g., "18:00")
  available: boolean;
  reason?: string; // Why it's unavailable (e.g., "fully_booked", "past_time", "outside_hours", "closed" = inside a vacation closed_range)
  remainingCapacity: number; // Number of remaining seats (0 if no max_occupancy set or unlimited)
  prePayment?: number; // Required prepayment amount if applicable
};

export type ReservationAvailability = {
  date: string; // ISO date string (YYYY-MM-DD)
  slots: TimeSlotAvailability[];
  closedReason?: "paused" | "vacation" | "closed"; // Why no slots are available
  closedGuestNote?: string; // Restaurant-authored note shown to guests when closed, or when a closed_range blocked some slots
};

export type ReservationComment = {
  id: string;
  reservation_id: string;
  user_id: number;
  user_name?: string | null;
  message: string;
  created_at: string;
  updated_at: string;
};

export type ReservationTag = {
  id: string;
  restaurant_id: number;
  name: string;
  color?: string | null;
  icon?: string | null;
  description?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};
